import { useSelector } from 'react-redux';
import { Toaster as Sonner, toast } from 'sonner';
import { HugeiconsIcon } from '@hugeicons/react';
import {
  CheckmarkCircle02Icon,
  InformationCircleIcon,
  Alert02Icon,
  MultiplicationSignCircleIcon,
  Loading03Icon,
} from '@hugeicons/core-free-icons';

// Errors and warnings need reading, so they outlast the 1.5s default. A call can still pass its
// own duration.
const PROBLEM_DURATION = { error: 6000, warning: 5000 };
Object.entries(PROBLEM_DURATION).forEach(([type, duration]) => {
  const show = toast[type];
  toast[type] = (message, options) => show(message, { duration, ...options });
});

const Toaster = ({ ...props }) => {
  // The dashboard's theme lives in Redux (app.isDarkTheme), not next-themes.
  const isDarkTheme = useSelector(state => state.app.isDarkTheme);
  const theme = isDarkTheme ? 'dark' : 'light';
  return (
    <Sonner
      theme={theme}
      className="toaster group"
      // Top right, clear of the 56px sticky headers (h-14) on every page.
      position="top-right"
      offset={{ top: 72, right: 24 }}
      mobileOffset={{ top: 64, right: 16, left: 16 }}
      icons={{
        success: <HugeiconsIcon icon={CheckmarkCircle02Icon} strokeWidth={2} className="size-4" />,
        info: <HugeiconsIcon icon={InformationCircleIcon} strokeWidth={2} className="size-4" />,
        warning: <HugeiconsIcon icon={Alert02Icon} strokeWidth={2} className="size-4" />,
        error: (
          <HugeiconsIcon icon={MultiplicationSignCircleIcon} strokeWidth={2} className="size-4" />
        ),
        loading: (
          <HugeiconsIcon icon={Loading03Icon} strokeWidth={2} className="size-4 animate-spin" />
        ),
      }}
      style={{
        '--normal-bg': 'var(--popover)',
        '--normal-text': 'var(--popover-foreground)',
        '--normal-border': 'var(--border)',
        '--border-radius': 'var(--radius)',
      }}
      // Every type shares the popover surface and foreground text; only the icon carries the
      // status colour. Errors stand out by their red icon and by staying up longer (6s).
      toastOptions={{
        classNames: {
          toast: 'cn-toast shadow-lg',
          description: 'text-muted-foreground!',
          // Actions (View, Undo) match the dashboard's brand buttons.
          actionButton:
            'h-7! rounded-full! bg-primary! px-3! text-xs! font-medium! text-primary-foreground! hover:bg-primary/90!',
          cancelButton:
            'h-7! rounded-full! bg-muted! px-3! text-xs! font-medium! text-foreground! hover:bg-muted/80!',
          success: '[&_[data-icon]]:text-success',
          info: '[&_[data-icon]]:text-info',
          warning: '[&_[data-icon]]:text-warning',
          error: '[&_[data-icon]]:text-destructive',
        },
      }}
      {...props}
    />
  );
};
export { Toaster };
